import { string, symbol } from 'zod';
import { HtmlElementWithFullscreen, DocumentWithFullscreen } from '../types/common';
import { LiquidationType, Order, OrderSide, PositionSide } from '../types/trade.d';
import { ReverseSide } from '../enum';
import { toNonExponential, VaildNumber } from './format';
import Big from 'big.js';
import isNumber from 'lodash/isNumber';
import isFunction from 'lodash/isFunction';
import differenceInSeconds from 'date-fns/differenceInSeconds';

Big.NE = -20;

export const isBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  } else {
    return true;
  }
};

export const isFullscreen = (): boolean => {
  if (document.fullscreenElement) {
    return true;
  } else {
    return false;
  }
};

export function openFullscreen(elem: HtmlElementWithFullscreen) {
  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.webkitRequestFullscreen) {
    /* Safari */
    elem.webkitRequestFullscreen();
  } else if (elem.msRequestFullscreen) {
    /* IE11 */
    elem.msRequestFullscreen();
  }
}

export function safe_openFullscreen(elem: HTMLElement) {
  setTimeout(() => openFullscreen(elem), 0);
}

export function closeFullscreen() {
  const doc = document as DocumentWithFullscreen;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    /* Safari */
    doc.webkitExitFullscreen();
  } else if (doc.msExitFullscreen) {
    /* IE11 */
    doc.msExitFullscreen();
  }
}

export function safe_closeFullscreen() {
  setTimeout(() => closeFullscreen(), 0);
}

export const isOnline = (): boolean => {
  if (isBrowser()) {
    return window.navigator.onLine;
  } else {
    return false;
  }
};

export const watchDarkMode = (cb: (mode: 'dark' | 'light') => void): void => {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (e.matches) {
      // Dark
      cb('dark');
    } else {
      // Light
      cb('light');
    }
  });
};

export const isEamil = (account: string) => {
  return string().email().safeParse(account).success;
};

export function isLocalhostInSafari() {
  if (isBrowser()) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !isIOS;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isSafari && isLocalhost;
  } else {
    return false;
  }
}

export function getPrecision(precision: string) {
  const number = toNonExponential(Number(precision));
  if (number) {
    if (number.includes('.')) {
      // 小数
      return number.slice(2).length;
    } else if (number === '1') {
      return 0;
    } else {
      return -(precision.length - 1);
    }
  } else {
    return 0;
  }
}

export function shortText(val: string, startSplit: number, endSplit: number, separator = '...') {
  const len = val.length;
  return startSplit + endSplit >= len
    ? val
    : val.slice(0, startSplit) + separator + (endSplit ? val.slice(-endSplit) : '');
}

/**
 * 对数字字符串格式化，不会进行任何预处理
 * 调用方需保证传入的数字是不带任何分隔符的标准数字字符串
 * @param value 数字字符串
 * @param digit 要保留的小数位数，超过的直接舍去
 * @param separator 是否需要千分位分隔符
 */
export function numberFormat(value: string, digit = 8, separator = true) {
  if (typeof value !== 'string') {
    return value;
  }
  const [int, decimal] = value.split('.');
  if (digit > 0) {
    if (separator) {
      const newInt = int.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
      return `${newInt}${decimal ? '.' + decimal.slice(0, digit) : ''}`;
    } else {
      return `${int}${decimal ? '.' + decimal.slice(0, digit) : ''}`;
    }
  } else {
    return `${int}`;
  }
}

export function roundToTick(price: string, tickSize: number): string {
  const priceNum = new Big(VaildNumber(price));
  const tickDecimals = Big(VaildNumber(tickSize)).toString().split('.')[1]?.length || 0;
  const tickMultiplier = new Big(10).pow(tickDecimals);
  const roundedNum = new Big(priceNum).div(tickSize).round(0, 3).times(tickSize);
  const roundedNumInt = roundedNum.times(tickMultiplier).round().div(tickMultiplier);
  return roundedNumInt.toFixed(tickDecimals);
}

function roundDownToTick(price: string, tickSize: number): string {
  const p = new Big(VaildNumber(price)); // 与 roundToTick 保持一致
  const t = new Big(VaildNumber(tickSize)); // 与 roundToTick 保持一致
  const tickDecimals = t.toString().split('.')[1]?.length || 0;
  const tickMultiplier = new Big(10).pow(tickDecimals);
  const floored = p.div(t).round(0, Big.roundDown).times(t);
  // 添加精度修正，与 roundToTick 保持一致
  const flooredInt = floored.times(tickMultiplier).round(0, Big.roundDown).div(tickMultiplier);

  return flooredInt.toFixed(tickDecimals);
}

/**
 * 当 tickerSize < 1 时，将价格精度规整到 tickerSize
 * @param price 价格字符串
 * @param tickSize 格式为 0.1 ｜ 0.05 等
 * @param direction 可选：'up'（向上取整），'down'（向下取整），默认 'up'
 */
export function formatNumberToTick(price: string, tickSize: number, direction: 'up' | 'down' = 'up'): string {
  if (price.includes('.') && tickSize < 1) {
    const priceDecimals = price.split('.')[1].length;
    const tickDecimals = Big(tickSize).toString().split('.')[1].length;

    if (priceDecimals >= tickDecimals) {
      if (direction === 'up') {
        // TODO 此处是大坑，千万别删。原因未知
        // roundToTick(price, tickSize);
        price = roundToTick(price, tickSize);
      } else {
        price = roundDownToTick(price, tickSize);
      }
    } else {
      return price;
    }
  }

  return price;
}

/**
 * Find the closest number that fits the tick size
 * @param a - Input number string
 * @param tickSize - Tick size string
 * @param isChooseTickerUpLimit - If true, round up; if false, round down
 * @returns Closest tick size number as string
 */
function findClosestTickSizeNumber(a: string, tickSize: string, isChooseTickerUpLimit: boolean): string {
  try {
    const price = new Big(a);
    const tick = new Big(tickSize);

    // Divide price by tick size to get quotient and remainder
    const quotient = price.div(tick);
    const quotientFloor = quotient.round(0, Big.roundDown); // Round down to get integer part

    // Calculate remainder
    const remainder = price.minus(quotientFloor.times(tick));

    // If remainder is 0, price is already aligned with tick size
    if (remainder.eq(0)) {
      return price.toFixed();
    }

    // Calculate lower and upper multiples
    const lowerMultiple = quotientFloor.times(tick);
    const upperMultiple = quotientFloor.plus(1).times(tick);

    if (isChooseTickerUpLimit) {
      return upperMultiple.toFixed();
    } else {
      return lowerMultiple.toFixed();
    }
  } catch (e) {
    console.error('Error in findClosestTickSizeNumber:', e);
    return '0';
  }
}

/**
 * Check if one number is greater than or equal to another
 * @param one - First number string
 * @param two - Second number string
 * @returns true if one >= two, false otherwise
 */
function isNumGreaterOrEqual(one: string, two: string): boolean {
  try {
    if (!one || !two) {
      return false;
    }

    const a = new Big(one);
    const b = new Big(two);

    return a.gte(b);
  } catch (e) {
    console.error('Error in isNumGreaterOrEqual:', e);
    return false;
  }
}

/**
 * Format price according to tick size
 * @param priceRaw - Raw price string
 * @param tickSize - Tick size string
 * @returns Formatted price string
 */
export function formatPriceToTick(priceRaw: string, tickSize: string | number): string {
  let priceFitTicks = priceRaw;
  tickSize = String(tickSize);
  if (isNumGreaterOrEqual(tickSize, '1')) {
    priceFitTicks = findClosestTickSizeNumber(priceRaw, tickSize, false);
  } else {
    priceFitTicks = findClosestTickSizeNumber(priceRaw, tickSize, true);
  }

  return priceFitTicks;
}

export function numberInputFormat(value: string, digit = 8, separator = true, tickSize?: number) {
  if (value === '' || value === null || value === undefined) {
    return value;
  }

  const formatedValue = stringReplacedPunctuation(value);

  let numberString = formatedValue.replace(/[^0-9.]/g, '');

  if (numberString.startsWith('0') && !numberString.includes('.')) {
    numberString = numberString.replace(/^0+/, '');
  }

  if (numberString.startsWith('.')) {
    numberString = numberString.replace('.', '0.');
  }

  if (Number(value) === 0) {
    if (digit <= 0) {
      return '';
    }
    if (value.includes('.')) {
      if (value.split('.')[1].length > digit - 1) {
        if (digit === 1) {
          return value.replace('.0', '.');
        } else {
          return value.replace('00', '0');
        }
      } else {
        return value;
      }
    } else {
      return value.replace('00', '0');
    }
  }

  if (digit <= 0 && numberString.includes('.')) {
    return numberString.split('.')[0];
  }

  // round to tickSize
  if (numberString.includes('.') && tickSize) {
    if (tickSize < 1) {
      if (numberString.split('.')[1].length >= toNonExponential(tickSize).split('.')[1].length) {
        numberString = roundToTick(numberString, tickSize);
      }
    }
  }

  if (
    ((numberString.indexOf('.') === numberString.length - 1 && numberString.endsWith('.')) ||
      ((numberString.match(/\./g) || []).length === 2 && numberString.endsWith('..'))) &&
    isNumber(Number(numberString))
  ) {
    return `${numberFormat(numberString, digit, separator)}.`;
  } else if (isNumber(Number(numberString))) {
    return numberFormat(numberString, digit, separator);
  } else {
    return '';
  }
}

/**
 * 支持负数的数字输入格式化
 * @param value - 输入值（可以包含负号）
 * @param digit - 小数位数精度
 * @param separator - 是否添加千分位分隔符
 * @param tickSize - 最小变动单位
 * @returns 格式化后的字符串
 */
export function numberInputFormatWithNegative(value: string, digit = 8, separator = true, tickSize?: number): string {
  if (value === '' || value === null || value === undefined) {
    return value;
  }

  // 检查是否以负号开头
  const isNegative = value.startsWith('-');

  // 如果只是一个负号，允许输入
  if (value === '-') {
    return '-';
  }

  // 去掉负号，使用原有的 numberInputFormat 处理
  const absoluteValue = isNegative ? value.substring(1) : value;

  // 调用原有的 numberInputFormat
  const formattedValue = numberInputFormat(absoluteValue, digit, separator, tickSize);

  // 如果格式化结果为空或者原始值是负数，重新添加负号
  if (formattedValue === '' || formattedValue === undefined) {
    return formattedValue;
  }

  return isNegative ? '-' + formattedValue : formattedValue;
}

/**
 * 按指定规则格式化数字输入（保留 fixed 模式下的一些特性）
 * 1. 替换逗号等标点为句号
 * 2. 保留 digit 位小数（若 digit = 0，则不保留小数）
 * 3. 若指定了 tickSize，则在小数部分达到 tickSize 的精度时进行四舍五入
 * 4. 保留部分用户的输入习惯，如以 "." 开头时自动补 "0."
 * 5. 处理“0”或连续输入“000”这类情况，尽量保证 UX
 */
export function numberInputFormat_FIXED(value: string, digit = 8, separator = true, tickSize?: number) {
  // 1. 空值或 null/undefined，直接返回
  if (value === '' || value == null) {
    return value;
  }

  // 2. 替换标点（,，。）为句号 .
  const replacedValue = stringReplacedPunctuation(value);

  // 3. 保留数字和小数点
  let numberString = replacedValue.replace(/[^0-9.]/g, '');

  // 3.1 如果只有小数点（可能用户刚输入 "." 或 "..." 等），补全 "0."
  if (numberString.startsWith('.')) {
    numberString = numberString.replace('.', '0.');
  }

  // 3.2 如果整数部分有多余的 0，如 "000123" => "123"
  //     但要确保包含小数点时，不要删除到前面没有 0
  if (numberString.startsWith('0') && !numberString.includes('.') && numberString.length > 1) {
    // 去除多余的前导 0
    numberString = numberString.replace(/^0+/, '');
  }

  // 4. 如果输入值本身对应数值 0（e.g. "0", "0.0", "00"）
  //    且 digit <= 0，则只能返回 "0"
  if (Number(value) === 0) {
    if (digit <= 0) {
      return '0';
    }
    // 有小数点的情况，根据保留位数处理
    if (value.includes('.')) {
      const [intPart, decimalPart = ''] = value.split('.');
      // 如果小数位长于 digit-1，就说明多输入了 0
      if (decimalPart.length > digit - 1) {
        if (digit === 1) {
          // 例如 "0.0" => "0."
          return value.replace('.0', '.');
        } else {
          // 例如 "0.00" => "0.0"
          return value.replace('00', '0');
        }
      }
      // 小数位数不多，保留即可
      return value;
    } else {
      // 没有小数点，就保证只留一个 "0"
      return value.replace(/^0+/, '0');
    }
  }

  // 5. digit <= 0 且包含小数点 => 仅保留整数部分
  if (digit <= 0 && numberString.includes('.')) {
    numberString = numberString.split('.')[0];
  }

  // 6. 若指定了 tickSize，则对超过 tickSize 的部分进行四舍五入
  if (numberString.includes('.') && tickSize && tickSize < 1) {
    const decimalLengthOfTick = toNonExponential(tickSize).split('.')[1]?.length ?? 0;
    const decimalLengthOfNum = numberString.split('.')[1]?.length ?? 0;
    // 当小数位数大于等于 tickSize 的小数精度时进行 round
    if (decimalLengthOfNum >= decimalLengthOfTick) {
      numberString = roundToTick(numberString, tickSize);
    }
  }

  // 7. 判断当前是否是处于“尚未完成输入”的场景
  //    比如用户刚输入 "3." 或 "3.." 等，允许在末尾保留一个点
  const dotCount = (numberString.match(/\./g) || []).length;
  const endsWithDotOrDoubleDot =
    (numberString.endsWith('.') && dotCount === 1) || (numberString.endsWith('..') && dotCount === 2);

  // 7.1 若处在尚未完成输入，但数值依然有效 => 保留末尾的 "."
  if (endsWithDotOrDoubleDot && isNumber(Number(numberString))) {
    return `${numberFormat(numberString, digit, separator)}.`;
  }

  // 8. 如果是正确数字，做常规格式化；否则返回空字符串
  if (isNumber(Number(numberString))) {
    return numberFormat(numberString, digit, separator);
  } else {
    return '';
  }
}

/**
 * 移除 string 中的「,，。」分隔符
 */
export function stringReplacedPunctuation(value: string) {
  if (value === '' || value === null || value === undefined) {
    return value;
  }
  const result = value.replace(/[,，。]/g, '.');
  if (/^[^\.]*\.[^\.]*\.$/.test(result)) {
    return result.slice(0, -1);
  }
  return result;
}

export function firstCharUpper(str: string) {
  return typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
}

/**
 * 多语言文本大小写转换工具（Locale-aware text case transformation）
 * 支持多种转换模式，针对不同语言环境自动适配（如土耳其语的特殊 i/I 转换）
 * @param str 输入字符串
 * @param caseType 转换模式：
 *   - 'upper': 全部大写（UPPERCASE）
 *   - 'lower': 全部小写（lowercase）
 *   - 'capitalize': 首字母大写，其余不变（Capitalize）
 *   - 'title': 每个单词首字母大写（Title Case）
 *   - 'sentence': 首字母大写，其余小写（Sentence case）
 * @param locale 目标语言区域（如 'en', 'zh-CN', 'tr'），默认 'en'
 * @returns 已按多语言规范转换的字符串
 * @example
 * 全部大写
 * toLocaleCase('hello world', 'upper') // "HELLO WORLD"
 * toLocaleCase('istanbul', 'upper', 'tr') // "İSTANBUL" (土耳其语: i → İ)
 *
 * 全部小写
 * toLocaleCase('HELLO WORLD', 'lower') // "hello world"
 * toLocaleCase('İSTANBUL', 'lower', 'tr') // "istanbul" (土耳其语: İ → i)
 *
 * 首字母大写（其余保持不变）
 * toLocaleCase('hello WORLD', 'capitalize') // "Hello WORLD"
 *
 * 每个单词首字母大写（Title Case）
 * toLocaleCase('hello world', 'title') // "Hello World"
 *
 * 句子格式（首字母大写，其余全部小写）
 * toLocaleCase('HELLO WORLD', 'sentence') // "Hello world"
 *
 * @note 多语言转换的副作用和注意事项：
 * 1. **非一对一映射**：转换不一定是一对一的字符映射，某些字符在转换时可能产生多个字符
 *    例如：德语 'ß' → 'SS'（小写转大写时变成两个字符）
 *
 * 2. **长度变化**：结果字符串的长度可能与输入长度不同
 *    例如：toLocaleCase('straße', 'upper', 'de') → "STRASSE" (6字符 → 7字符)
 *
 * 3. **转换不稳定**：由于上述原因，转换操作不具有稳定性，以下表达式可能返回 false
 *    x.toLocaleLowerCase() === x.toLocaleUpperCase().toLocaleLowerCase()
 *    示例：'ß' → 'SS' → 'ss' (不等于原始的 'ß')
 *
 * 4. **语言特定行为**：不同语言的转换规则差异很大
 *    - 土耳其语：'i' ↔ 'İ', 'ı' ↔ 'I' (与英语不同)
 *    - 德语：'ß' → 'SS' (上) 但 'SS' → 'ss' (下)
 *    - 希腊语：'Σ' → 'σ' 或 'ς' (取决于位置)
 *
 * 5. **性能考虑**：多语言转换比简单的 ASCII 转换慢，在处理大量文本时需注意
 */
export function toLocaleCase(
  str: string,
  caseType: 'upper' | 'lower' | 'capitalize' | 'title' | 'sentence',
  locale: string = 'en'
): string {
  if (!str) return '';

  switch (caseType) {
    case 'upper':
      // 全部大写（使用 toLocaleUpperCase 支持特殊语言，如土耳其语的 i → İ）
      return str.toLocaleUpperCase(locale);

    case 'lower':
      // 全部小写（使用 toLocaleLowerCase 支持特殊语言，如土耳其语的 İ → i）
      return str.toLocaleLowerCase(locale);

    case 'capitalize':
      // 第一个字母大写，其余保持不变
      // 使用 charAt + slice 方式，兼容所有语言（中文、日文、阿拉伯语等）
      return str.charAt(0).toLocaleUpperCase(locale) + str.slice(1);

    case 'title':
      // 每个单词首字母大写（Title Case）
      // 按空格分割单词，每个单词首字母大写
      return str
        .split(/(\s+)/)
        .map((word) => {
          if (!word || /^\s+$/.test(word)) return word;
          return word.charAt(0).toLocaleUpperCase(locale) + word.slice(1);
        })
        .join('');

    case 'sentence':
      // 第一个字母大写，其余全部小写（句子格式）
      return str.charAt(0).toLocaleUpperCase(locale) + str.slice(1).toLocaleLowerCase(locale);

    default:
      return str;
  }
}

export { isMobile } from './isMobile';

export function isSafari() {
  let isSafari = 'Safari';
  if (isFunction(window.browser)) {
    isSafari = window.browser().browser;
  }
  if (isSafari === 'Safari') {
    return true;
  } else {
    return false;
  }
}

/**
 * 安全地访问 sessionStorage，处理在 Safari 等浏览器中可能因隐私设置而不可用的情况
 * @param key - 存储键名
 * @returns 存储的值，如果不可用则返回 null
 */
export function safeSessionStorageGet(key: string): string | null {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage !== null) {
      return sessionStorage.getItem(key);
    }
  } catch (error) {
    console.warn('sessionStorage is not available:', error);
  }
  return null;
}

/**
 * 安全地设置 sessionStorage，处理在 Safari 等浏览器中可能因隐私设置而不可用的情况
 * @param key - 存储键名
 * @param value - 存储的值
 * @returns 是否设置成功
 */
export function safeSessionStorageSet(key: string, value: string): boolean {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage !== null) {
      sessionStorage.setItem(key, value);
      return true;
    }
  } catch (error) {
    console.warn('sessionStorage is not available:', error);
  }
  return false;
}

/**
 * 安全地删除 sessionStorage 中的项，处理在 Safari 等浏览器中可能因隐私设置而不可用的情况
 * @param key - 存储键名
 * @returns 是否删除成功
 */
export function safeSessionStorageRemove(key: string): boolean {
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage !== null) {
      sessionStorage.removeItem(key);
      return true;
    }
  } catch (error) {
    console.warn('sessionStorage is not available:', error);
  }
  return false;
}

export function detectDeviceType(userAgent: string): string {
  if (/android/i.test(userAgent)) {
    return 'Android';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'iOS';
  } else {
    return 'Pc';
  }
}

export function uniqueArray(arr: any[], uniId: string) {
  const res = new Map();
  return arr.filter((item) => !res.has(item[uniId]) && res.set(item[uniId], 1));
}

export function clearlAllIntervals(intervals: any) {
  for (const interval of intervals) {
    clearInterval(interval);
  }
}

export function formatStringToValidNumber(numberString: string): string {
  // 如果以小数点开头,在开头补零
  if (numberString.startsWith('.')) {
    return '0' + numberString;
  }

  // 包含小数点
  if (numberString.includes('.')) {
    const parts = numberString.split('.');
    const intPart = parts[0];
    const decPart = parts[1];

    // 整数部分有前导零,去掉前导零
    if (intPart.startsWith('0') && intPart.length > 1) {
      return intPart.replace(/^0+/, '') + '.' + decPart;
    } else {
      return numberString;
    }
  }

  return numberString;
}

export function convertSymbolForSpot(symbol: string) {
  let base = '',
    quote = '',
    sym = '';
  if (typeof symbol === 'string') {
    sym = symbol.replace('-', '/');
    [base = '', quote = ''] = sym.split('/');
  }
  return { base, quote, spotSymbol: sym };
}

export function getBrowserLocale() {
  if (navigator.languages != undefined) return navigator.languages[0];
  return navigator.language;
}

export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

type ParseUrlReturn<T extends string | undefined> = T extends string ? string : Record<string, string>;
export function parseUrlParam<T extends string | undefined>(url: string, key?: T): ParseUrlReturn<T> {
  const params: string[] | undefined = url?.split('?')?.[1]?.split('&');
  const obj: Record<string, string> = {};
  if (params) {
    params.forEach((item) => {
      const [key, value] = item.split('=');
      obj[key] = value || '';
    });
  }

  if (key === undefined) {
    return obj as ParseUrlReturn<T>;
  }
  return (obj[key] || '') as ParseUrlReturn<T>;
}

function toRedirect(destination: string) {
  return {
    redirect: {
      destination,
      permanent: false,
    },
  } as const;
}

export function toRedirectNoPermission() {
  return toRedirect(`${window.location.origin}/forbidden`);
}

export function toConvertSide(args: { position_side: PositionSide; order_side: OrderSide }): {
  side: 'long' | 'short' | 'buy' | 'sell';
  isLongSide: boolean;
  reverseSide: ReverseSide;
} {
  args.position_side = args.position_side || 'net';
  const lowerPosSide = args.position_side.toLowerCase() as Lowercase<PositionSide>;
  if (lowerPosSide === 'net') {
    const side = args.order_side.toLowerCase() as Lowercase<OrderSide>;
    return {
      side,
      isLongSide: side === 'buy',
      reverseSide: ReverseSide[side],
    };
  }
  return {
    side: lowerPosSide,
    isLongSide: lowerPosSide === 'long',
    reverseSide: ReverseSide[lowerPosSide],
  };
}

export function toConvertSideOfLang(args: { position_side: PositionSide; order_side: OrderSide; prefix?: string }) {
  args.position_side = args.position_side || 'net';
  const lowerPosSide = args.position_side.toLowerCase() as Lowercase<PositionSide>;
  const lowerOrdSide = args.order_side.toLowerCase() as Lowercase<OrderSide>;
  if (lowerPosSide === 'net') {
    return lowerOrdSide;
  }
  if ((lowerPosSide === 'long' && lowerOrdSide === 'buy') || (lowerPosSide === 'short' && lowerOrdSide === 'sell')) {
    return args.prefix ? `${args.prefix}_open_${lowerPosSide}` : `open_${lowerPosSide}`;
  }
  return args.prefix ? `${args.prefix}_close_${lowerPosSide}` : `close_${lowerPosSide}`;
}

export function diffTime(start: number, end: number) {
  const diff = differenceInSeconds(end, start);
  const days = Math.floor(diff / 86400)
    .toString()
    .padStart(2, '0');
  const hours = Math.floor((diff % 86400) / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((diff % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (diff % 60).toString().padStart(2, '0');
  return { days, hours, minutes, seconds };
}

export function convertStringToArray(str: string | string[] = '') {
  return typeof str === 'string' ? (str ? str.replace(/；/g, ';').split(';') : []) : str;
}

export const isLiquidationOrder = (orderCategory: string) => {
  return orderCategory === 'liquidation' || orderCategory === 'partial_liquidation';
};

export const toLiquidationSide = (side: Lowercase<OrderSide>, liquidationType: LiquidationType) => {
  if (liquidationType === 'liquidation') {
    return side === 'buy' ? 'liquidationOfBuy' : 'liquidationOfSell';
  } else {
    return side === 'buy' ? 'partiallyLiquidationBuy' : 'partiallyLiquidationSell';
  }
};

export const getUserViewType = (cookieUserViewType?: string) => {
  const userViewType = cookieUserViewType || 'real-trading';
  return {
    userViewType: userViewType,
    isDemoTrading: Boolean(userViewType === 'demo-trading'),
  };
};

// 支持负精度：当 precision < 0 时，按 10 的 |precision| 次幂向下截取到整数倍
export const toFixedWithNegativeSupport = (value: Big | string | number, precision: number, rm?: number): string => {
  const num = new Big(value as any);
  if (precision >= 0) {
    return num.toFixed(precision, (rm || 0) as Big.RoundingMode);
  }
  const factor = new Big(10).pow(-precision);
  return num
    .div(factor)
    .round(0, (rm || 0) as Big.RoundingMode)
    .times(factor)
    .toFixed(0, 0);
};
